import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import CustomCalendar from './CustomCalendar';
import api from '../utils/api';
import '../styles/Dashboard.css';
import '../styles/theme.css';

const Dashboard = () => {
    const [employees, setEmployees] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(moment());
    const [currentDate, setCurrentDate] = useState(moment());
    const [schedules, setSchedules] = useState([]);
    const [viewMode, setViewMode] = useState('week');
    const [activeTab, setActiveTab] = useState('dashboard');
    const [searchTerm, setSearchTerm] = useState('');
    const [taskSearchTerm, setTaskSearchTerm] = useState('');
    const navigate = useNavigate();

    const getRandomPastelColor = (seed) => {
        const colors = [
            '#2196F3', // blue
            '#9C27B0', // purple
            '#4CAF50', // green
            '#FF9800', // orange
            '#E91E63', // pink
            '#00BCD4', // cyan
            '#009688', // teal
            '#607D8B'  // blue gray
        ];
        const index = Math.abs(seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % colors.length;
        return colors[index];
    };

    const renderAvatar = (employee) => {
        const initial = employee.first_name?.charAt(0)?.toUpperCase() || 
                       employee.email?.charAt(0)?.toUpperCase();
        const backgroundColor = getRandomPastelColor(employee.email);
        
        return initial ? (
            <div 
                key={employee.id} 
                className="avatar" 
                title={employee.email}
                style={{ backgroundColor }}
            >
                {initial}
            </div>
        ) : null;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setError('No authentication token found');
                    setLoading(false);
                    return;
                }

                const headers = {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                };

                // Fetch employees, company info, schedules, and requests in parallel
                const [employeesResponse, companyResponse, schedulesResponse, requestsResponse] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/employees`, { headers }),
                    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/companies/info`, { headers }),
                    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/schedules`, { headers }),
                    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/requests/company`, { headers })
                ]);

                console.log('Employees:', employeesResponse.data);
                console.log('Company:', companyResponse.data);
                console.log('Raw schedules data:', schedulesResponse.data);
                console.log('Requests data:', requestsResponse.data);
                
                // Debug: Check if any schedules have zoom_info
                const schedulesWithZoom = schedulesResponse.data.filter(schedule => schedule.zoom_info);
                console.log('Schedules with zoom_info:', schedulesWithZoom);
                
                // Create a mapping between user_ids and employee IDs
                const userIdToEmployeeId = {};
                
                employeesResponse.data.forEach(employee => {
                    if (employee.user_id) {
                        userIdToEmployeeId[employee.user_id] = employee.id;
                    }
                });

                // Convert schedules to calendar format
                const formattedSchedules = schedulesResponse.data.map(schedule => {
                    const mappedEmployeeId = userIdToEmployeeId[schedule.user_id];
                    
                    // Debug: Log schedule with zoom_info
                    if (schedule.zoom_info) {
                        console.log('🎯 Schedule with zoom_info found:', {
                            id: schedule.id,
                            title: schedule.title,
                            zoom_info: schedule.zoom_info
                        });
                    }
                    
                    // Parse the shift_date to get just the date part
                    const shiftDate = schedule.shift_date?.split('T')[0];
                    if (!shiftDate || !schedule.start_time || !schedule.end_time) {
                        console.warn('Invalid schedule data:', schedule);
                        return null;
                    }

                    const startTime = moment(`${shiftDate} ${schedule.start_time}`);
                    const endTime = moment(`${shiftDate} ${schedule.end_time}`);
                    
                    if (!startTime.isValid() || !endTime.isValid()) {
                        console.warn('Invalid date/time in schedule:', schedule);
                        return null;
                    }

                    return {
                        id: schedule.id,
                        calendarId: schedule.calendar_type || 'tasks',
                        title: schedule.title || 'Untitled Schedule',
                        start: startTime.toDate(),
                        end: endTime.toDate(),
                        resource: mappedEmployeeId ? String(mappedEmployeeId) : undefined,
                        color: schedule.color || '#4b8dff',
                        backgroundColor: schedule.color || '#4b8dff',
                        files: schedule.files || [],
                        description: schedule.description || '',
                        location: schedule.location || '',
                        isAllDay: schedule.is_all_day || false,
                        zoom_info: schedule.zoom_info || null,
                        raw: {
                            participants: schedule.participants || []
                        }
                    };
                }).filter(Boolean); // Remove any null entries

                // Convert approved requests to calendar events
                const approvedRequests = requestsResponse.data.filter(request => request.status === 'approved');
                console.log('Approved requests:', approvedRequests);

                const requestEvents = [];
                approvedRequests.forEach(request => {
                    const mappedEmployeeId = userIdToEmployeeId[request.user_id];
                    if (!mappedEmployeeId) {
                        console.warn('No employee mapping found for user_id:', request.user_id);
                        return;
                    }

                    // Generate events for each day in the request range
                    const startDate = moment(request.start_date);
                    const endDate = moment(request.end_date);
                    const currentDate = startDate.clone();

                    while (currentDate.isSameOrBefore(endDate, 'day')) {
                        // Create PTO event as 8-hour block (9am-5pm)
                        const eventStart = currentDate.clone().hour(9).minute(0).second(0);
                        const eventEnd = currentDate.clone().hour(17).minute(0).second(0);

                        // Determine title and color based on PTO status
                        let title, color, backgroundColor;
                        if (request.is_pto) {
                            title = `PTO - ${request.request_type.replace('_', ' ')}`;
                            color = '#10B981'; // Green for PTO
                            backgroundColor = '#10B981';
                        } else {
                            title = `Time Off - ${request.request_type.replace('_', ' ')}`;
                            color = '#F59E0B'; // Orange for regular time off
                            backgroundColor = '#F59E0B';
                        }

                        requestEvents.push({
                            id: `request-${request.id}-${currentDate.format('YYYY-MM-DD')}`,
                            calendarId: 'time-off',
                            title: title,
                            start: eventStart.toDate(),
                            end: eventEnd.toDate(),
                            resource: String(mappedEmployeeId),
                            color: color,
                            backgroundColor: backgroundColor,
                            description: request.reason || '',
                            isAllDay: false,
                            isPTO: request.is_pto || false,
                            requestType: request.request_type,
                            requestId: request.id,
                            countsTowardHours: request.is_pto || false,
                            raw: {
                                participants: []
                            }
                        });

                        currentDate.add(1, 'day');
                    }
                });

                console.log('Generated request events:', requestEvents);
                
                // Combine schedules and request events
                const allEvents = [...formattedSchedules, ...requestEvents];
                console.log('All formatted events:', allEvents);
                setSchedules(allEvents);

                if (!employeesResponse.data || !Array.isArray(employeesResponse.data)) {
                    throw new Error('Invalid employees data received');
                }

                console.log('Employees response:', employeesResponse.data);
                setEmployees(employeesResponse.data);
                
                if (!companyResponse.data) {
                    throw new Error('Invalid company data received');
                }
                
                console.log('Company info response:', companyResponse.data);
                setCompanyInfo(companyResponse.data);

            } catch (error) {
                console.error('Error fetching data:', error);
                if (error.response) {
                    console.error('Error response:', error.response.data);
                    if (error.response.status === 401) {
                        localStorage.removeItem('token'); // Clear invalid token
                        setError('Session expired. Please log in again.');
                    } else {
                        setError(error.response.data?.message || error.response.data?.error || 'Failed to fetch data');
                    }
                } else if (error.request) {
                    console.error('No response received:', error.request);
                    setError('Network error. Please check your connection.');
                } else {
                    console.error('Error details:', error);
                    setError(error.message || 'An unexpected error occurred');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleScheduleCreate = async (eventData, formData = null) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Authentication token missing');
            }

            // If formData is provided, we're handling file uploads
            if (formData) {
                console.log('📎 Creating schedule with file uploads');
                
                // Use the regular /api/schedules endpoint with FormData
                const response = await api.post('/api/schedules', formData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                console.log('Schedule with files created successfully:', response.data);
                
                // Add the new schedule to the state with proper time handling
                const createdSchedule = {
                    id: response.data.id,
                    calendarId: response.data.type || 'tasks',
                    title: response.data.title,
                    start: moment(`${response.data.shift_date} ${response.data.start_time}`).toDate(),
                    end: moment(`${response.data.shift_date} ${response.data.end_time}`).toDate(),
                    resource: String(eventData.resource),
                    description: response.data.description,
                    location: response.data.location,
                    isAllDay: response.data.is_all_day,
                    color: response.data.color || '#4b8dff',
                    backgroundColor: response.data.color || '#4b8dff',
                    files: response.data.files || [], // Include files in the schedule
                    zoom_info: response.data.zoom_info || null,
                    raw: {
                        participants: response.data.participants || eventData.participants || []
                    }
                };

                setSchedules(prev => [...prev, createdSchedule]);
                return response.data.id;
            }

            // Regular schedule creation without files
            // Validate required fields
            if (!eventData.user_id || !eventData.company_id || !eventData.shift_date || !eventData.start_time || !eventData.end_time) {
                console.error('Missing required fields:', eventData);
                throw new Error('Missing required fields for schedule creation');
            }

            // Validate date formats
            if (!moment(eventData.shift_date, 'YYYY-MM-DD', true).isValid()) {
                throw new Error('Invalid shift date format');
            }
            if (!moment(eventData.start_time, 'HH:mm:ss', true).isValid()) {
                throw new Error('Invalid start time format');
            }
            if (!moment(eventData.end_time, 'HH:mm:ss', true).isValid()) {
                throw new Error('Invalid end time format');
            }

            console.log('Creating new schedule:', eventData);

            // Create schedule object with required fields
            const newSchedule = {
                user_id: eventData.user_id,
                company_id: eventData.company_id,
                shift_date: eventData.shift_date,
                start_time: eventData.start_time,
                end_time: eventData.end_time,
                title: eventData.title || 'Work Hours',
                type: eventData.type || 'shift',
                description: eventData.description || '',
                is_all_day: eventData.is_all_day || false,
                location: eventData.location || '',
                status: eventData.status || 'confirmed',
                color: eventData.color || '#4b8dff',
                organizer: eventData.organizer || '',
                source: eventData.source || 'optivo',
                calendar: eventData.calendar || 'Optivo',
                zoom_info: eventData.zoom_info || null,
                participants: eventData.participants || []
            };

            console.log('Sending schedule creation request:', newSchedule);
            
            const response = await api.post('/api/schedules', newSchedule);
            console.log('Schedule created successfully:', response.data);

            // Add the new schedule to the state with proper time handling
            const createdSchedule = {
                id: response.data.id,
                calendarId: response.data.type || 'tasks',
                title: response.data.title,
                start: moment(`${response.data.shift_date} ${response.data.start_time}`).toDate(),
                end: moment(`${response.data.shift_date} ${response.data.end_time}`).toDate(),
                resource: String(eventData.resource),
                description: response.data.description,
                location: response.data.location,
                isAllDay: response.data.is_all_day,
                color: response.data.color || '#4b8dff',
                backgroundColor: response.data.color || '#4b8dff',
                zoom_info: response.data.zoom_info || null,
                raw: {
                    participants: response.data.participants || eventData.participants || []
                }
            };

            setSchedules(prev => [...prev, createdSchedule]);
            return response.data.id;
        } catch (error) {
            console.error('Error creating schedule:', {
                error: error.message,
                response: error.response?.data,
                status: error.response?.status,
                fullError: error
            });
            
            let errorMessage = 'Failed to create schedule';
            if (error.response?.data?.errors) {
                errorMessage = error.response.data.errors.join(', ');
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            throw new Error(errorMessage);
        }
    };

    const renderMiniCalendar = () => {
        const startDay = currentMonth.clone().startOf('month').startOf('week');
        const endDay = currentMonth.clone().endOf('month').endOf('week');
        const calendar = [];
        const day = startDay.clone();

        while (day.isSameOrBefore(endDay, 'day')) {
            calendar.push(day.clone());
            day.add(1, 'day');
        }

        return (
            <div style={styles.miniCalendar}>
                <div style={styles.miniCalendarHeader}>
                    <div style={styles.miniCalendarMonthYearHeader}>
                        <div style={{ display: 'flex', alignItems: 'baseline' }}>
                            <span style={styles.monthText}>{currentMonth.format('MMMM')}</span>
                            <span style={styles.yearText}>{currentMonth.format('YYYY')}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button 
                                style={styles.miniCalendarButton}
                                onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, 'month'))}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M15.2484 6.35159C15.4734 6.57662 15.5998 6.88179 15.5998 7.19999C15.5998 7.51818 15.4734 7.82335 15.2484 8.04839L11.2968 12L15.2484 15.9516C15.467 16.1779 15.588 16.481 15.5852 16.7957C15.5825 17.1103 15.4563 17.4113 15.2338 17.6338C15.0113 17.8563 14.7103 17.9825 14.3957 17.9852C14.0811 17.9879 13.7779 17.867 13.5516 17.6484L8.75161 12.8484C8.52665 12.6234 8.40027 12.3182 8.40027 12C8.40027 11.6818 8.52665 11.3766 8.75161 11.1516L13.5516 6.35159C13.7766 6.12662 14.0818 6.00024 14.4 6.00024C14.7182 6.00024 15.0234 6.12662 15.2484 6.35159Z" fill="white"/>
                                </svg>
                            </button>
                            <button 
                                style={styles.miniCalendarButton}
                                onClick={() => setCurrentMonth(currentMonth.clone().add(1, 'month'))}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'scaleX(-1)' }}>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M15.2484 6.35159C15.4734 6.57662 15.5998 6.88179 15.5998 7.19999C15.5998 7.51818 15.4734 7.82335 15.2484 8.04839L11.2968 12L15.2484 15.9516C15.467 16.1779 15.588 16.481 15.5852 16.7957C15.5825 17.1103 15.4563 17.4113 15.2338 17.6338C15.0113 17.8563 14.7103 17.9825 14.3957 17.9852C14.0811 17.9879 13.7779 17.867 13.5516 17.6484L8.75161 12.8484C8.52665 12.6234 8.40027 12.3182 8.40027 12C8.40027 11.6818 8.52665 11.3766 8.75161 11.1516L13.5516 6.35159C13.7766 6.12662 14.0818 6.00024 14.4 6.00024C14.7182 6.00024 15.0234 6.12662 15.2484 6.35159Z" fill="white"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div style={styles.miniCalendarDayNames}>
                        {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                            <div key={day} style={styles.dayName}>{day}</div>
                        ))}
                    </div>
                </div>
                <div style={styles.miniCalendarDays}>
                    {calendar.map(date => (
                        <div
                            key={date.format('YYYY-MM-DD')}
                            style={{
                                ...styles.calendarDay,
                                ...(date.isSame(moment(), 'day') && styles.today),
                                ...(date.month() !== currentMonth.month() && styles.otherMonth)
                            }}
                        >
                            {date.format('D')}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const handleTabClick = (tab, event) => {
        if (event) {
            event.preventDefault();
        }
        setActiveTab(tab);
        // Add navigation logic here when we create the Employees page
        if (tab === 'employees') {
            navigate('/employees');
        } else if (tab === 'requests') {
            navigate('/requests');
        }
    };

    const handleNavigation = (route) => {
        navigate(route);
    };

    // Add function to handle view toggle
    const handleViewToggle = () => {
        // Get the token from localStorage
        const token = localStorage.getItem('token') || localStorage.getItem('employeeToken');
        
        if (token) {
            // Store the token in both localStorage and sessionStorage for the employee app
            localStorage.setItem('employeeToken', token);
            sessionStorage.setItem('employeeToken', token);
            
            // Navigate to the employee calendar
            window.location.href = 'https://calendar.joinoptivo.com';
        } else {
            console.error('No token found for navigation');
        }
    };

    // Add refreshSchedules function
    const refreshSchedules = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('No token found for refresh');
            return;
        }

        const headers = {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        };

        try {
            console.log('🔄 Refreshing schedules and requests...');
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';
            
            const [schedulesResponse, requestsResponse] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/schedules`, { headers }),
                axios.get(`${API_BASE_URL}/api/requests/company`, { headers })
            ]);
            
            if (!schedulesResponse.data || !Array.isArray(schedulesResponse.data)) {
                throw new Error('Invalid schedules data received during refresh');
            }

            // Create a mapping between user_ids and employee IDs
            const userIdToEmployeeId = {};
            employees.forEach(employee => {
                if (employee.user_id) {
                    userIdToEmployeeId[employee.user_id] = employee.id;
                }
            });

            // Convert schedules to calendar format
            const formattedSchedules = schedulesResponse.data.map(schedule => {
                const mappedEmployeeId = userIdToEmployeeId[schedule.user_id];
                
                const shiftDate = schedule.shift_date?.split('T')[0];
                if (!shiftDate || !schedule.start_time || !schedule.end_time) {
                    console.warn('Invalid schedule data:', schedule);
                    return null;
                }

                const startTime = moment(`${shiftDate} ${schedule.start_time}`);
                const endTime = moment(`${shiftDate} ${schedule.end_time}`);
                
                if (!startTime.isValid() || !endTime.isValid()) {
                    console.warn('Invalid date/time in schedule:', schedule);
                    return null;
                }

                return {
                    id: schedule.id,
                    calendarId: schedule.calendar_type || 'tasks',
                    title: schedule.title || 'Untitled Schedule',
                    start: startTime.toDate(),
                    end: endTime.toDate(),
                    resource: mappedEmployeeId ? String(mappedEmployeeId) : undefined,
                    color: schedule.color || '#4b8dff',
                    backgroundColor: schedule.color || '#4b8dff',
                    files: schedule.files || [],
                    description: schedule.description || '',
                    location: schedule.location || '',
                    isAllDay: schedule.is_all_day || false,
                    zoom_info: schedule.zoom_info || null,
                    raw: {
                        participants: schedule.participants || []
                    }
                };
            }).filter(Boolean);

            // Convert approved requests to calendar events
            const approvedRequests = requestsResponse.data.filter(request => request.status === 'approved');
            console.log('🔄 Approved requests during refresh:', approvedRequests);

            const requestEvents = [];
            approvedRequests.forEach(request => {
                const mappedEmployeeId = userIdToEmployeeId[request.user_id];
                if (!mappedEmployeeId) {
                    console.warn('No employee mapping found for user_id:', request.user_id);
                    return;
                }

                // Generate events for each day in the request range
                const startDate = moment(request.start_date);
                const endDate = moment(request.end_date);
                const currentDate = startDate.clone();

                while (currentDate.isSameOrBefore(endDate, 'day')) {
                    // Create PTO event as 8-hour block (9am-5pm)
                    const eventStart = currentDate.clone().hour(9).minute(0).second(0);
                    const eventEnd = currentDate.clone().hour(17).minute(0).second(0);

                    // Determine title and color based on PTO status
                    let title, color, backgroundColor;
                    if (request.is_pto) {
                        title = `PTO - ${request.request_type.replace('_', ' ')}`;
                        color = '#10B981'; // Green for PTO
                        backgroundColor = '#10B981';
                    } else {
                        title = `Time Off - ${request.request_type.replace('_', ' ')}`;
                        color = '#F59E0B'; // Orange for regular time off
                        backgroundColor = '#F59E0B';
                    }

                    requestEvents.push({
                        id: `request-${request.id}-${currentDate.format('YYYY-MM-DD')}`,
                        calendarId: 'time-off',
                        title: title,
                        start: eventStart.toDate(),
                        end: eventEnd.toDate(),
                        resource: String(mappedEmployeeId),
                        color: color,
                        backgroundColor: backgroundColor,
                        description: request.reason || '',
                        isAllDay: false,
                        isPTO: request.is_pto || false,
                        requestType: request.request_type,
                        requestId: request.id,
                        countsTowardHours: request.is_pto || false,
                        raw: {
                            participants: []
                        }
                    });

                    currentDate.add(1, 'day');
                }
            });

            // Combine schedules and request events
            const allEvents = [...formattedSchedules, ...requestEvents];
            console.log('🔄 Schedules and requests refreshed:', allEvents.length, 'total events found');
            setSchedules(allEvents);
        } catch (error) {
            console.error('Error refreshing schedules:', error);
        }
    };

    // Day navigation functions
    const goToPreviousDay = () => {
        const amount = viewMode === 'week' ? 7 : 1;
        const unit = viewMode === 'week' ? 'week' : 'day';
        const newDate = moment(currentDate).subtract(amount, viewMode === 'week' ? 'days' : 'day');
        console.log(`Previous ${unit} clicked - navigating from:`, currentDate.format('YYYY-MM-DD'), 'to:', newDate.format('YYYY-MM-DD'));
        setCurrentDate(newDate);
    };

    const goToNextDay = () => {
        const amount = viewMode === 'week' ? 7 : 1;
        const unit = viewMode === 'week' ? 'week' : 'day';
        const newDate = moment(currentDate).add(amount, viewMode === 'week' ? 'days' : 'day');
        console.log(`Next ${unit} clicked - navigating from:`, currentDate.format('YYYY-MM-DD'), 'to:', newDate.format('YYYY-MM-DD'));
        setCurrentDate(newDate);
    };

    const goToToday = () => {
        const today = moment();
        console.log('Today button clicked - navigating from:', currentDate.format('YYYY-MM-DD'), 'to today:', today.format('YYYY-MM-DD'));
        setCurrentDate(today);
    };

    // Filter employees based on search term
    const filteredEmployees = employees.filter(employee => {
        if (!searchTerm.trim()) return true;
        
        const searchLower = searchTerm.toLowerCase();
        const firstName = employee.first_name?.toLowerCase() || '';
        const lastName = employee.last_name?.toLowerCase() || '';
        const email = employee.email?.toLowerCase() || '';
        const role = employee.role?.toLowerCase() || '';
        
        return firstName.includes(searchLower) ||
               lastName.includes(searchLower) ||
               email.includes(searchLower) ||
               role.includes(searchLower) ||
               `${firstName} ${lastName}`.includes(searchLower);
    });

    // Filter tasks/schedules based on task search term
    const filteredSchedules = schedules.filter(schedule => {
        if (!taskSearchTerm.trim()) return schedule;
        
        const searchLower = taskSearchTerm.toLowerCase();
        const title = schedule.title?.toLowerCase() || '';
        const description = schedule.description?.toLowerCase() || '';
        const location = schedule.location?.toLowerCase() || '';
        
        // Also search by employee name if we can find the employee
        let employeeName = '';
        if (schedule.resource) {
            const employee = employees.find(emp => emp.id === parseInt(schedule.resource));
            if (employee) {
                const firstName = employee.first_name?.toLowerCase() || '';
                const lastName = employee.last_name?.toLowerCase() || '';
                employeeName = `${firstName} ${lastName}`.trim();
            }
        }
        
        return title.includes(searchLower) ||
               description.includes(searchLower) ||
               location.includes(searchLower) ||
               employeeName.includes(searchLower);
    });

    const styles = {
        container: {
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '8px',
            boxShadow: 'none',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            zIndex: 10,
            marginTop: '0',
        },
        mainContent: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            marginTop: '0',
            zIndex: 10,
        },
        timeHeaderWrapper: {
            backgroundColor: 'var(--bg-primary)',
            zIndex: 2,
            overflow: 'hidden',
            borderBottom: '1px solid var(--border-color)',
            width: '100%',
            display: 'flex',
            height: '70px',
            position: 'relative',
        },
        teamColumnSpace: {
            width: '280px',
            flexShrink: 0,
            backgroundColor: 'var(--bg-primary)',
            borderRight: '1px solid var(--border-color)',
            height: '70px',
            position: 'relative',
            zIndex: 3,
            overflow: 'hidden',
        },
        timeHeader: {
            display: 'flex',
            height: '70px',
            backgroundColor: 'var(--bg-primary)',
            width: viewMode === 'week' ? '100%' : '1700px',
            position: 'relative',
            transform: 'translateX(0)',
            transition: 'transform 0ms linear',
            flex: 1,
            overflow: 'hidden',
        },
        timeHeaderCell: {
            width: viewMode === 'week' ? 'calc(100% / 7)' : '100px',
            flexShrink: 0,
            borderRight: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--bg-primary)',
            height: viewMode === 'week' ? '80px' : '70px',
            transition: 'all 0.2s ease',
            padding: '4px',
        },
        timeContentWrapper: {
            width: viewMode === 'week' ? '100%' : '70px',
            height: viewMode === 'week' ? '100%' : '70px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            padding: viewMode === 'week' ? '8px' : '4px',
        },
        timeNumber: {
            fontSize: '24px',
            fontWeight: '500',
            lineHeight: '1',
            marginBottom: '6px',
            textAlign: 'center',
            display: 'block',
            color: 'var(--text-primary)',
        },
        timePeriod: {
            fontSize: '14px',
            color: 'var(--text-secondary)',
            lineHeight: '1',
            textAlign: 'center',
            display: 'block',
        },
        employeeCell: {
            padding: '0 12px',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            backgroundColor: 'var(--bg-primary)',
            height: viewMode === 'week' ? '180px' : '80px',
            minHeight: viewMode === 'week' ? '180px' : '80px',
            transition: 'all 0.2s ease',
            minWidth: '0',
        },
        employeeName: {
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        employeeRole: {
            fontSize: '12px',
            color: 'var(--text-secondary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        timeSlot: {
            width: viewMode === 'week' ? 'calc(100% / 7)' : '100px',
            flexShrink: 0,
            borderRight: '1px solid var(--border-color)',
            position: 'relative',
            backgroundColor: 'var(--bg-primary)',
            height: '100%',
            padding: viewMode === 'week' ? '8px' : '4px',
            display: viewMode === 'week' ? 'flex' : 'block',
            flexDirection: 'column',
            gap: '8px',
        },
        event: {
            position: viewMode === 'week' ? 'relative' : 'absolute',
            top: viewMode === 'week' ? 'auto' : '0',
            left: viewMode === 'week' ? 'auto' : '4px',
            right: viewMode === 'week' ? 'auto' : '4px',
            height: viewMode === 'week' ? '80%' : '100%',
            backgroundColor: '#E8F0FD',
            color: '#1760EB',
            border: '1px solid #1760EB',
            borderRadius: '6px',
            padding: viewMode === 'week' ? '8px 12px' : '4px 8px',
            fontSize: viewMode === 'week' ? '14px' : '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: viewMode === 'week' ? 'auto' : 'calc(100% - 8px)',
            boxShadow: viewMode === 'week' ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
            margin: viewMode === 'week' ? '4px 0' : '0',
            zIndex: 1,
        },
        miniCalendar: {
            width: '300px',
            padding: '16px 16px 16px 24px',
            backgroundColor: 'transparent',
            borderRadius: '8px',
        },
        miniCalendarHeader: {
            marginBottom: '16px',
        },
        miniCalendarMonthYearHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
        },
        monthText: {
            fontSize: '32px',
            fontWeight: '600',
            color: 'var(--text-primary)',
        },
        yearText: {
            fontSize: '32px',
            color: '#EF4444',
            fontWeight: '600',
            marginLeft: '8px',
        },
        miniCalendarDayNames: {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            textAlign: 'center',
            marginBottom: '0',
            paddingBottom: '0',
            borderBottom: 'none'
        },
        dayName: {
            color: '#666',
            fontSize: '12px',
            padding: '1px',
            fontWeight: '500',
        },
        miniCalendarDays: {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            marginTop: '0',
            marginBottom: '16px',
        },
        calendarDay: {
            padding: '8px 4px',
            textAlign: 'center',
            fontSize: '14px',
            color: '#333',
            cursor: 'pointer',
            position: 'relative',
        },
        today: {
            backgroundColor: '#EF4444',
            color: 'white',
            borderRadius: '6px',
        },
        otherMonth: {
            color: '#ccc',
        },
        miniCalendarButton: {
            border: 'none',
            background: 'none',
            padding: '8px',
            cursor: 'pointer',
            color: '#666',
            fontSize: '18px',
            '&:hover': {
                color: '#4263eb',
            },
        },
        loading: {
            textAlign: 'center',
            padding: '2rem',
            color: '#666',
        },
    };

    // Add dark theme by default
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', 'dark');
    }, []);

    if (loading) {
        return <div style={styles.loading}>Loading...</div>;
    }

    return (
        <div className="dashboard">
            <div className="dashboard-header">
                <div className="dashboard-title-row">
                    <div className="mini-calendar">
                        {renderMiniCalendar()}
                    </div>
                </div>
            </div>
            
            <div className="top-header">
                <h1 style={{ 
                    color: '#C0BFBF', 
                    fontFamily: 'Albert Sans, sans-serif',
                    fontWeight: '700',
                    fontSize: '48px',
                    marginTop: '8px',
                    marginLeft: '24px'
                }}>{companyInfo?.name}</h1>
                
                <div style={{ 
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '24px',
                    marginLeft: '-100px'  // Pull it more to the left
                }}>
                    {/* View Toggle Switch replaces navigation */}
                    <div style={{
                        width: '200px',
                        height: '44px',
                        backgroundColor: '#2D2D2D',
                        borderRadius: '22px',
                        padding: '4px',
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative'
                    }} onClick={handleViewToggle}>
                        <div style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            width: 'calc(50% - 4px)',
                            height: '36px',
                            backgroundColor: '#1E1E1E',
                            borderRadius: '18px',
                            transition: 'all 0.3s ease',
                            zIndex: 1
                        }}></div>
                        <div style={{
                            position: 'relative',
                            display: 'flex',
                            height: '36px',
                            width: '100%',
                            zIndex: 2
                        }}>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'rgba(255, 255, 255, 0.6)',
                                fontWeight: '500',
                                fontSize: '14px',
                                transition: 'color 0.3s ease'
                            }}>Calendar</div>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: '500',
                                fontSize: '14px',
                                transition: 'color 0.3s ease'
                            }}>Manager</div>
                        </div>
                    </div>
                </div>

                {/* Task Search Box - Far Right Corner */}
                <div className="task-search-box" style={{ 
                    position: 'absolute',
                    right: '24px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '250px',
                    display: 'flex',
                    alignItems: 'center',
                    background: 'var(--bg-secondary)',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    zIndex: 2
                }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', flexShrink: 0 }}>
                        <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 14L11 11" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input 
                        type="text" 
                        placeholder="Search Tasks" 
                        value={taskSearchTerm}
                        onChange={(e) => setTaskSearchTerm(e.target.value)}
                        style={{
                            border: 'none',
                            outline: 'none',
                            width: '100%',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                            fontSize: '14px',
                            fontFamily: 'Inter, sans-serif'
                        }}
                    />
                </div>
            </div>

            <div className="company-info">
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: '100%', 
                    position: 'relative' 
                }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        marginLeft: '5px'
                    }}>
                        {/* Day Navigation Controls */}
                        <div className="week-nav" style={{ marginRight: '24px' }}>
                            <button
                                className="nav-button"
                                onClick={goToPreviousDay}
                                title={viewMode === 'week' ? 'Previous week' : 'Previous day'}
                            >
                                ‹
                            </button>
                            <button
                                className="today-button"
                                onClick={goToToday}
                                title="Go to today"
                            >
                                Today
                            </button>
                            <button
                                className="nav-button"
                                onClick={goToNextDay}
                                title={viewMode === 'week' ? 'Next week' : 'Next day'}
                            >
                                ›
                            </button>
                        </div>

                        {/* New Task Button */}
                        <button style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: '#2D2D2D',
                            color: 'rgba(255, 255, 255, 0.6)',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            transition: 'all 0.3s ease',
                            marginRight: '48px',
                            whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#353535'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#2D2D2D'}
                        onClick={() => {
                            // Handle new task creation
                            console.log('New task button clicked');
                        }}
                        >
                            New Task +
                        </button>
                        
                        {/* Day/Week Toggle Slider */}
                        <div style={{
                            width: '200px',
                            height: '36px',
                            backgroundColor: '#2D2D2D',
                            borderRadius: '8px',
                            padding: '3px',
                            boxSizing: 'border-box',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            position: 'relative',
                            marginRight: '64px'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: '3px',
                                left: viewMode === 'day' ? '3px' : 'calc(50% - 3px)',
                                width: 'calc(50% - 3px)',
                                height: '30px',
                                backgroundColor: '#353535',
                                borderRadius: '6px',
                                transition: 'all 0.3s ease',
                                zIndex: 1
                            }}></div>
                            <div style={{
                                position: 'relative',
                                display: 'flex',
                                height: '30px',
                                width: '100%',
                                zIndex: 2
                            }}>
                                <div 
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: viewMode === 'day' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                        fontWeight: '500',
                                        fontSize: '13px',
                                        transition: 'color 0.3s ease',
                                        cursor: 'pointer',
                                        padding: '0 16px'
                                    }}
                                    onClick={() => setViewMode('day')}
                                >
                                    Day
                                </div>
                                <div 
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: viewMode === 'week' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                        fontWeight: '500',
                                        fontSize: '13px',
                                        transition: 'color 0.3s ease',
                                        cursor: 'pointer',
                                        padding: '0 16px'
                                    }}
                                    onClick={() => setViewMode('week')}
                                >
                                    Week
                                </div>
                            </div>
                        </div>
                        <div className="sub-nav-menu" style={{ marginLeft: '32px', display: 'flex', alignItems: 'center' }}>
                            <a 
                                href="#" 
                                className={`sub-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                                onClick={(event) => handleTabClick('dashboard', event)}
                                style={{
                                    color: activeTab === 'dashboard' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    marginRight: '32px',
                                    transition: 'color 0.3s ease'
                                }}
                            >
                                Dashboard
                            </a>
                            <a 
                                href="#" 
                                className={`sub-nav-item ${activeTab === 'employees' ? 'active' : ''}`}
                                onClick={(event) => handleTabClick('employees', event)}
                                style={{
                                    color: activeTab === 'employees' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    marginRight: '32px',
                                    transition: 'color 0.3s ease'
                                }}
                            >
                                Employees
                            </a>
                            <a 
                                href="#" 
                                className={`sub-nav-item ${activeTab === 'requests' ? 'active' : ''}`}
                                onClick={(event) => handleTabClick('requests', event)}
                                style={{
                                    color: activeTab === 'requests' ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                    textDecoration: 'none',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    marginRight: '32px',
                                    transition: 'color 0.3s ease'
                                }}
                            >
                                Requests
                            </a>

                            {/* Search Bar */}
                            <div style={{
                                width: '300px',
                                display: 'flex',
                                alignItems: 'center',
                                background: 'var(--bg-secondary)',
                                borderRadius: '6px',
                                padding: '8px 12px',
                                border: '1px solid var(--border-color)',
                                marginLeft: '-8px',
                                marginRight: '48px'
                            }}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', flexShrink: 0 }}>
                                    <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M14 14L11 11" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <input 
                                    type="text" 
                                    placeholder="Search for Team Members" 
                                    style={{
                                        border: 'none',
                                        outline: 'none',
                                        width: '100%',
                                        background: 'transparent',
                                        color: 'var(--text-primary)',
                                        fontSize: '14px',
                                        fontFamily: 'Inter, sans-serif'
                                    }}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="people-avatars" style={{ marginLeft: 'auto', width: '400px', display: 'flex', justifyContent: 'flex-end' }}>
                        {filteredEmployees.map(renderAvatar)}
                        <div 
                            className="avatar add-manager" 
                            style={{ 
                                border: `2px dashed var(--border-color)`,
                                backgroundColor: 'transparent',
                                color: 'var(--text-secondary)',
                                cursor: 'pointer'
                            }}
                            title="Add Manager"
                        >
                            +
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="dashboard-content">
                <div className="calendar-section">
                    <CustomCalendar
                        employees={filteredEmployees}
                        schedules={filteredSchedules}
                        setSchedules={setSchedules}
                        onScheduleCreate={handleScheduleCreate}
                        viewMode={viewMode}
                        refreshSchedules={refreshSchedules}
                        currentDate={currentDate}
                    />
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
